/** Geometry materialization and GLB baking; source execution belongs to the host. */
import { Scene } from '@babylonjs/core/scene';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData';
import { CSG } from '@babylonjs/core/Meshes/csg';
import { PBRMaterial } from '@babylonjs/core/Materials/PBR/pbrMaterial';
import { MultiMaterial } from '@babylonjs/core/Materials/multiMaterial';
import { SubMesh } from '@babylonjs/core/Meshes/subMesh';
import { Color3, Color4 } from '@babylonjs/core/Maths/math.color';
import { Vector2, Vector3, Vector4, Matrix, Quaternion } from '@babylonjs/core/Maths/math.vector';
import { ANCHOR_PREFIX, anchorRotation } from '../../exports/editor';
import { Path2, Path3D, Curve3 } from '@babylonjs/core/Maths/math.path';
import { Axis, Space } from '@babylonjs/core/Maths/math.axis';
import { VertexBuffer } from '@babylonjs/core/Buffers/buffer';
import { GLTF2Export } from '@babylonjs/serializers';

import '@babylonjs/core/Meshes/Builders/boxBuilder';
import '@babylonjs/core/Meshes/Builders/sphereBuilder';
import '@babylonjs/core/Meshes/Builders/cylinderBuilder';
import '@babylonjs/core/Meshes/Builders/planeBuilder';
import '@babylonjs/core/Meshes/Builders/groundBuilder';
import '@babylonjs/core/Meshes/Builders/torusBuilder';
import '@babylonjs/core/Meshes/Builders/torusKnotBuilder';
import '@babylonjs/core/Meshes/Builders/discBuilder';
import '@babylonjs/core/Meshes/Builders/latheBuilder';
import '@babylonjs/core/Meshes/Builders/tubeBuilder';
import '@babylonjs/core/Meshes/Builders/ribbonBuilder';
import '@babylonjs/core/Meshes/Builders/shapeBuilder';
import '@babylonjs/core/Meshes/Builders/polygonBuilder';
import '@babylonjs/core/Meshes/Builders/polyhedronBuilder';
import '@babylonjs/core/Meshes/Builders/icoSphereBuilder';
import '@babylonjs/core/Meshes/Builders/capsuleBuilder';
import '@babylonjs/core/Meshes/Builders/tiledBoxBuilder';
import '@babylonjs/core/Meshes/Builders/tiledPlaneBuilder';

import { buildIdioms } from './mesh-idioms';
import { buildModifiers } from './mesh-modifiers';
import { triangulate } from './mesh-earcut';
import { polygonOptions } from './mesh-polygon';
import { diagnoseMeshes, diagnosticWarnings, type MeshDiagnostics } from './mesh-diagnostics';
import { normalizeBaseColor, normalizeEmissive, type SpatialMaterialSpec } from './spatial-material';

/** Triangles across everything the build leaves alive. */
export const MAX_TRIANGLES = 200_000;
const ANCHOR_NAME = /^[A-Za-z0-9_.-](?:[A-Za-z0-9 _.-]{0,46}[A-Za-z0-9_.-])?$/;
/** Build time budget shared with the host executor. */
export const MAX_BUILD_MS = 20_000;
const MAX_EXPORT_MESHES = 4_000;
const MAX_LIVE_MESHES = 20_000;
const MAX_ANCHORS = 256;

export interface MeshBuildStats {
    /** Bounded read-only prototype evidence, not a validity certificate. */
    diagnostics?: MeshDiagnostics;
    triangles: number;
    meshes: number;
    /** Distinct material names on the exported meshes, in creation order. */
    materialSlots: string[];
    /** World-space AABB of everything exported, metres. */
    bounds: { min: [number, number, number]; max: [number, number, number]; size: [number, number, number] };
    /** Names of the connector anchors baked with the part, in declaration order. */
    anchors?: string[];
}

export interface MeshBuildResult {
    glb: Blob | null;
    stats: MeshBuildStats | null;
    /** Empty on success; one short materialization or export error otherwise. */
    errors: string[];
    warnings: string[];
}

const EMPTY_STATS: MeshBuildStats = {
    triangles: 0, meshes: 0, materialSlots: [],
    bounds: { min: [0, 0, 0], max: [0, 0, 0], size: [0, 0, 0] },
};

/** Everything a builder call can be handed as `options`. */
type BuilderOptions = Record<string, unknown>;

interface MeshApi {
    scratch: Scene;
    api: Record<string, unknown>;
    /** Meshes still alive, in creation order. */
    live: () => Mesh[];
    /** Connector anchors declared with anchor(), baked as empty nodes. */
    anchors: TransformNode[];
    triangles: () => number;
}

function isMesh(value: unknown): value is Mesh {
    return value instanceof Mesh;
}

/** Flatten whatever the model returned into the meshes it meant. */
function collectReturned(value: unknown, into: Set<Mesh>): void {
    if (isMesh(value)) { into.add(value); return; }
    if (Array.isArray(value)) { for (const v of value) collectReturned(v, into); }
}

type AllocatingBase = { new (): object } & Record<string, unknown>;

function withAllocatingStatics<T extends object>(Base: T): T {
    const base = Base as unknown as AllocatingBase;
    const allocating: Record<string, unknown> = {};
    for (const name of Object.getOwnPropertyNames(base)) {
        if (!name.endsWith('ToRef')) continue;
        const bare = name.slice(0, -'ToRef'.length);
        if (!bare || bare in (base as object)) continue;
        const toRef = base[name];
        if (typeof toRef !== 'function') continue;
        allocating[bare] = (...args: unknown[]): object => {
            const out = new base();
            (toRef as (...rest: unknown[]) => unknown)(...args, out);
            return out;
        };
    }
    if (Object.keys(allocating).length === 0) return Base;
    return new Proxy(Base, {
        get: (target, prop) => (typeof prop === 'string' && prop in allocating && !(prop in target)
            ? allocating[prop]
            : Reflect.get(target, prop)),
        has: (target, prop) => (typeof prop === 'string' && prop in allocating) || Reflect.has(target, prop),
    });
}

const MERGE_ATTRIBUTES = [
    VertexBuffer.NormalKind, VertexBuffer.UVKind, VertexBuffer.UV2Kind, VertexBuffer.UV3Kind,
    VertexBuffer.UV4Kind, VertexBuffer.UV5Kind, VertexBuffer.UV6Kind, VertexBuffer.ColorKind,
    VertexBuffer.TangentKind, VertexBuffer.MatricesIndicesKind, VertexBuffer.MatricesWeightsKind,
    VertexBuffer.MatricesIndicesExtraKind, VertexBuffer.MatricesWeightsExtraKind,
];

function normalizeAttributesForMerge(meshes: Mesh[]): void {
    const union = MERGE_ATTRIBUTES.filter(kind => meshes.some(m => m.isVerticesDataPresent(kind)));
    if (!union.length) return;
    for (const mesh of meshes) {
        const vertices = mesh.getTotalVertices();
        for (const kind of union) {
            if (mesh.isVerticesDataPresent(kind)) continue;
            const data = new Float32Array(vertices * VertexBuffer.DeduceStride(kind));
            if (kind === VertexBuffer.NormalKind) {
                const positions = mesh.getVerticesData(VertexBuffer.PositionKind);
                const indices = mesh.getIndices();
                if (positions && indices) VertexData.ComputeNormals(positions, indices, data, { useRightHandedSystem: mesh.sideOrientation === 1 });
            } else if (kind === VertexBuffer.ColorKind) {
                data.fill(1); // white = no tint, the same default Babylon uses
            }
            mesh.setVerticesData(kind, data, false);
        }
    }
}

export const UNPAINTED_SLOT = 'mat_unpainted';

/** Give every material-less mesh the shared default slot. Returns their names. */
function paintUnpainted(meshes: Mesh[], scratch: Scene): string[] {
    const bare = meshes.filter(mesh => !mesh.material);
    if (bare.length === 0) return [];
    let slot = scratch.materials.find(m => m.name === UNPAINTED_SLOT) as PBRMaterial | undefined;
    if (!slot) {
        slot = new PBRMaterial(UNPAINTED_SLOT, scratch);
        slot.albedoColor = new Color3(0.8, 0.8, 0.8);
        slot.metallic = 0;
        slot.roughness = 0.6;
    }
    for (const mesh of bare) mesh.material = slot;
    return bare.map(mesh => mesh.name);
}

/** One short warning naming the pieces, capped so a 300-piece build stays readable. */
function warnUnpainted(names: string[], where: string, warnings: string[]): void {
    if (names.length === 0) return;
    const shown = names.slice(0, 6).join(', ');
    const rest = names.length > 6 ? `, +${names.length - 6} more` : '';
    warnings.push(
        `${where}: ${names.length} piece(s) had no material() and were given the shared "${UNPAINTED_SLOT}" slot (${shown}${rest}). `
        + 'An unpainted piece otherwise inherits the first material in the merge. Assign material() per piece for per-face colour.',
    );
}

function buildApi(scratch: Scene, deadline: number, warnings: string[]): MeshApi {
    const created: Mesh[] = [];
    const booleanOperands = new Set<Mesh>();
    let triangles = 0;
    let materialCounter = 0;

    let nextCensus = MAX_LIVE_MESHES;

    const checkBudget = (): void => {
        if (Date.now() > deadline) {
            throw new Error(`Build time limit reached (${Math.round(MAX_BUILD_MS / 1000)}s). Build fewer, larger pieces.`);
        }
        if (triangles > MAX_TRIANGLES) {
            throw new Error(`Triangle budget exceeded (${triangles.toLocaleString()} > ${MAX_TRIANGLES.toLocaleString()} live working triangles, including reusable Boolean operands). Lower tessellation/segments, or split the build.`);
        }
        if (created.length > nextCensus) {
            const alive = created.reduce((n, m) => (m.isDisposed() ? n : n + 1), 0);
            if (alive > MAX_LIVE_MESHES) {
                throw new Error(
                    `Live mesh limit reached (${MAX_LIVE_MESHES.toLocaleString()} pieces alive at once). `
                    + 'merge() each batch as you finish it — or use coursedWall()/shingles()/plankedSlab()/spokes(), which merge for you.',
                );
            }
            nextCensus = created.length + (MAX_LIVE_MESHES - alive);
        }
    };

    /** Adopt a freshly built mesh: count it, budget it, hand it back. */
    const register = (mesh: Mesh): Mesh => {
        created.push(mesh);
        triangles += mesh.getTotalIndices() / 3;
        checkBudget();
        return mesh;
    };

    /** Rebill a mesh rebuilt in place: welding can drop triangles, subdividing adds them. */
    const retally = (mesh: Mesh, indicesBefore: number): Mesh => {
        triangles += (mesh.getTotalIndices() - indicesBefore) / 3;
        checkBudget();
        return mesh;
    };

    const forget = (mesh: Mesh): void => {
        if (!mesh.isDisposed()) triangles -= mesh.getTotalIndices() / 3;
    };

    const STANDARD_BUILDERS = [
        'CreateBox', 'CreateSphere', 'CreateCylinder', 'CreatePlane', 'CreateGround',
        'CreateTiledGround', 'CreateTiledBox', 'CreateTiledPlane', 'CreateDisc',
        'CreateTorus', 'CreateTorusKnot', 'CreateLathe', 'CreateTube', 'CreateRibbon',
        'CreateIcoSphere', 'CreateCapsule', 'CreatePolyhedron',
        'ExtrudeShape', 'ExtrudeShapeCustom',
    ] as const;
    const POLYGON_BUILDERS = ['CreatePolygon', 'ExtrudePolygon'] as const;

    const builders: Record<string, unknown> = {};
    for (const key of STANDARD_BUILDERS) {
        const fn = (MeshBuilder as unknown as Record<string, unknown>)[key];
        if (typeof fn !== 'function') continue;
        builders[key] = (name: string, options?: BuilderOptions) =>
            register((fn as (n: string, o: BuilderOptions, s: Scene) => Mesh)(name, options ?? {}, scratch));
    }
    for (const key of POLYGON_BUILDERS) {
        const fn = (MeshBuilder as unknown as Record<string, unknown>)[key];
        if (typeof fn !== 'function') continue;
        builders[key] = (name: string, options?: BuilderOptions) =>
            register((fn as (n: string, o: BuilderOptions, s: Scene, e: unknown) => Mesh)(
                name, polygonOptions(options ?? {}), scratch, triangulate,
            ));
    }

    const material = (
        first: (SpatialMaterialSpec & { name?: string }) | string = {},
        second?: SpatialMaterialSpec,
    ): PBRMaterial => {
        const spec: SpatialMaterialSpec & { name?: string } = (typeof first === 'string' ? second : first) ?? {};
        const label = (typeof first === 'string' ? first : spec.name)?.trim();
        const mat = new PBRMaterial(label || `mat_${++materialCounter}`, scratch);
        const color = spec.color ? normalizeBaseColor(spec.color) : null;
        // sRGB hex → LINEAR albedo, because the exporter writes albedoColor
        // straight into baseColorFactor and glTF says that field is linear.
        mat.albedoColor = color
            ? Color3.FromHexString(color.slice(0, 7)).toLinearSpace()
            : new Color3(0.8, 0.8, 0.8);
        mat.metallic = typeof spec.metallic === 'number' ? Math.min(1, Math.max(0, spec.metallic)) : 0;
        mat.roughness = typeof spec.roughness === 'number' ? Math.min(1, Math.max(0, spec.roughness)) : 0.6;
        if (spec.emissive) {
            const emissive = normalizeEmissive(spec.emissive);
            if (emissive) mat.emissiveColor = Color3.FromHexString(emissive).toLinearSpace();
        }
        const alphaFromColor = color && color.length === 9 ? parseInt(color.slice(7, 9), 16) / 255 : 1;
        const opacity = typeof spec.opacity === 'number' ? Math.min(1, Math.max(0, spec.opacity)) : alphaFromColor;
        if (opacity < 1) {
            mat.alpha = opacity;
            mat.transparencyMode = PBRMaterial.PBRMATERIAL_ALPHABLEND;
        }
        return mat;
    };

    // ── VertexData / from_pydata ──
    // The authoring boundary is glTF-style CCW, independently of Babylon's
    // native builders (CW indices with outward normals in a RH scene).
    const meshFromVertexData = (name: string, data: Partial<VertexData>, options: { winding?: 'ccw' | 'cw' } = {}): Mesh => {
        const winding = options.winding ?? 'ccw';
        if (winding !== 'ccw' && winding !== 'cw') throw new Error('winding must be ccw or cw');
        if (!data || typeof data !== 'object' || !data.positions) {
            throw new Error('meshFromVertexData requires positions and optional indices/normals/UV buffers (VertexData or a plain data object).');
        }
        // A numeric-buffer record is a natural JS authoring input. Normalize it
        // through Babylon's own parser, then clone so winding conversion and
        // baking cannot mutate buffers the author may reuse for another part.
        const native = (data instanceof VertexData ? data : VertexData.Parse(data)).clone();
        if (data.positions) {
            const indices = data.indices ? Array.from(data.indices) : Array.from({ length: data.positions.length / 3 }, (_, i) => i);
            if (indices.length % 3) throw new Error('meshFromVertexData requires triangle indices or unindexed triangles');
            if (winding === 'ccw') for (let i = 0; i < indices.length; i += 3) [indices[i + 1], indices[i + 2]] = [indices[i + 2], indices[i + 1]];
            native.indices = indices;
            if (!data.normals && data.positions) {
                const normals: number[] = [];
                VertexData.ComputeNormals(data.positions, indices, normals);
                native.normals = normals;
            }
        }
        const mesh = new Mesh(name, scratch);
        native.applyToMesh(mesh);
        return register(mesh);
    };


    const fromPydata = (
        name: string,
        verts: (number[] | Vector3)[] | number[],
        faces: number[][],
        options?: {
            normals?: number[]; uvs?: number[]; flat?: boolean;
            materials?: PBRMaterial[]; materialIndices?: number[];
            winding?: 'ccw' | 'cw';
        },
    ): Mesh => {
        const positions: number[] = [];
        if (verts.length && typeof verts[0] === 'number') {
            positions.push(...(verts as number[]));
        } else {
            for (const v of verts as (number[] | Vector3)[]) {
                if (Array.isArray(v)) positions.push(v[0], v[1], v[2]);
                else positions.push(v.x, v.y, v.z);
            }
        }

        const slots = options?.materials?.filter(Boolean) ?? [];
        const perFace = options?.materialIndices;
        if (perFace && slots.length === 0) {
            throw new Error('fromPydata(name, verts, faces, {materialIndices}) needs a materials: [...] array for the indices to point into.');
        }

        const indices: number[] = [];
        const emit = (face: number[]): void => {
            for (let i = 1; i < face.length - 1; i++) indices.push(face[0], face[i], face[i + 1]);
        };
        const ranges: { material: PBRMaterial; start: number; count: number }[] = [];
        if (perFace) {
            const slotOf = (f: number): number => {
                const raw = Math.round(perFace[f] ?? 0);
                return Number.isFinite(raw) ? Math.min(Math.max(raw, 0), slots.length - 1) : 0;
            };
            for (let s = 0; s < slots.length; s++) {
                const start = indices.length;
                for (let f = 0; f < faces.length; f++) if (slotOf(f) === s) emit(faces[f]);
                if (indices.length > start) ranges.push({ material: slots[s], start, count: indices.length - start });
            }
        } else {
            for (const face of faces) emit(face);
        }

        const data = new VertexData();
        data.positions = positions;
        data.indices = indices;
        if (options?.uvs) data.uvs = options.uvs;
        if (options?.normals) data.normals = options.normals;
        const mesh = meshFromVertexData(name, data, options);

        if (ranges.length === 1) {
            mesh.material = ranges[0].material;
        } else if (ranges.length > 1) {
            const multi = new MultiMaterial(`${name}_slots`, scratch);
            multi.subMaterials = ranges.map(r => r.material);
            mesh.material = multi;
            // applyToMesh gave it one global submesh over everything; replace
            // that with one per slot, each over its own index range.
            mesh.releaseSubMeshes();
            const vertexCount = positions.length / 3;
            ranges.forEach((r, i) => SubMesh.AddToMesh(i, 0, vertexCount, r.start, r.count, mesh));
        } else if (slots.length === 1) {
            mesh.material = slots[0];
        }
        return mesh;
    };

    const boolean = (op: 'subtract' | 'union' | 'intersect') =>
        (a: Mesh, b: Mesh, name?: string): Mesh => {
            if (!isMesh(a) || !isMesh(b)) throw new Error(`${op}(a, b) takes two meshes`);
            for (const mesh of [a, b]) {
                if (mesh.isDisposed() || !mesh.getTotalVertices()) {
                    throw new Error(`${op}: operand "${mesh.name}" has no live geometry. Use a live mesh; merge() disposes its inputs.`);
                }
            }
            const csg = CSG.FromMesh(a)[op](CSG.FromMesh(b));
            const out = csg.toMesh(name ?? a.name, a.material, scratch);
            // Keep operands reusable, but don't bake cutters/intermediate solids
            // when a script relies on the implicit return. Explicit returns win.
            booleanOperands.add(a); booleanOperands.add(b);
            return register(out);
        };

    // ── merge / named ──
    const merge = (meshes: Mesh[] | Mesh, name?: string): Mesh => {
        const given = (Array.isArray(meshes) ? meshes : [meshes]).filter(isMesh);
        const list = given.filter(m => !m.isDisposed() && m.getTotalVertices() > 0);
        if (!list.length) throw new Error('merge([...]) needs at least one mesh with geometry');
        if (list.length < given.length) {
            warnings.push(`merge() skipped ${given.length - list.length} piece(s) with no geometry (already merged, consumed by a boolean, or empty).`);
        }
        if (list.length === 1) return name ? named(list[0], name) : list[0];
        // Every piece carries its own slot BEFORE the merge, or Babylon hands
        // the unpainted ones the first material in the list (see UNPAINTED_SLOT).
        warnUnpainted(paintUnpainted(list, scratch), 'merge()', warnings);
        // Uniform attributes and one winding, or MergeMeshes refuses the lot.
        normalizeAttributesForMerge(list);
        const orientation = list[0].sideOrientation;
        for (const m of list) {
            if (m.sideOrientation !== orientation) {
                m.makeGeometryUnique();
                m.flipFaces(false); // Preserve outward normals and material slots.
                m.sideOrientation = orientation;
            }
        }
        // Drop the sources from the tally BEFORE the merge: MergeMeshes
        // disposes them itself, and a disposed mesh reports no indices, so
        // forgetting them afterwards would subtract nothing and every merged
        // build would be billed twice against the triangle budget.
        for (const m of list) forget(m);
        // multiMultiMaterials: one mesh, one submesh per source material —
        // this is the per-face material slot that costs build_spatial one
        // object per colour patch.
        const merged = Mesh.MergeMeshes(list, true, true, undefined, false, true);
        if (!merged) {
            throw new Error('merge() could not combine these meshes. Instances and clones made with createInstance() cannot merge — build each piece as its own mesh.');
        }
        merged.sideOrientation = orientation;
        if (name) merged.name = name;
        return register(merged);
    };

    const named = (mesh: Mesh, name: string): Mesh => {
        if (!isMesh(mesh)) throw new Error('named(mesh, name) takes a mesh');
        mesh.name = name;
        return mesh;
    };

    /** Recompute smooth normals: weld coincident vertices, average across them. */
    const smooth = (mesh: Mesh): Mesh => {
        if (!isMesh(mesh)) throw new Error('smooth(mesh) takes a mesh');
        // forceSharedVertices welds by position and rebuilds ONE submesh over
        // everything, so a mesh carrying per-face slots would come out of it
        // wearing whichever material won. Say so rather than eat 27 of them.
        if (mesh.material && 'subMaterials' in mesh.material) {
            throw new Error(
                'smooth(mesh) welds vertices, which collapses the per-material slots on this mesh. '
                + 'smooth() each piece BEFORE merge(), or before handing the faces to fromPydata.',
            );
        }
        const before = mesh.getTotalIndices();
        mesh.forceSharedVertices();
        return retally(mesh, before);
    };

    /** Flat-shaded: every facet gets its own normals. Triangle count unchanged. */
    const flat = (mesh: Mesh): Mesh => {
        if (!isMesh(mesh)) throw new Error('flat(mesh) takes a mesh');
        const before = mesh.getTotalIndices();
        mesh.convertToFlatShadedMesh();
        return retally(mesh, before);
    };

    // A connector the part mates by: an empty node whose +Y points out of the
    // connector (up out of a stud, out of a socket's mouth). See anchors.ts.
    const anchors: TransformNode[] = [];
    const anchor = (name: unknown, at: unknown, direction: unknown = [0, 1, 0]): TransformNode => {
        const vec = (v: unknown, what: string): Vector3 => {
            const a = v instanceof Vector3 ? v.asArray() : v;
            if (!Array.isArray(a) || a.length !== 3 || !a.every(n => typeof n === 'number' && Number.isFinite(n))) {
                throw new Error(`anchor(${JSON.stringify(name)}): ${what} must be [x, y, z] numbers.`);
            }
            return new Vector3(a[0] as number, a[1] as number, a[2] as number);
        };
        if (typeof name !== 'string' || !ANCHOR_NAME.test(name)) throw new Error(`anchor name ${JSON.stringify(name)} must be 1-48 letters, digits, spaces, _ . or -.`);
        if (anchors.some(a => a.name === ANCHOR_PREFIX + name)) throw new Error(`anchor "${name}" is declared twice; give each connector its own name.`);
        if (anchors.length >= MAX_ANCHORS) throw new Error(`More than ${MAX_ANCHORS} anchors; declare the connectors a placement needs, not every surface point.`);
        const out = vec(direction, 'direction');
        if (out.length() < 1e-9) throw new Error(`anchor("${name}"): direction must not be zero.`);
        const node = new TransformNode(ANCHOR_PREFIX + name, scratch);
        node.position = vec(at, 'position');
        node.rotationQuaternion = anchorRotation(out.normalize());
        anchors.push(node);
        return node;
    };

    const api: Record<string, unknown> = {
        anchor,
        MeshBuilder: builders,
        VertexData,
        Mesh,
        CSG, CSG2: CSG,
        subtract: boolean('subtract'),
        union: boolean('union'),
        intersect: boolean('intersect'),
        material,
        merge,
        named,
        smooth,
        flat,
        meshFromVertexData,
        fromPydata,
        // Proxied so the allocating spelling of every `…ToRef` static is there
        // (`Quaternion.FromUnitVectors`, `Matrix.FromQuaternion`,
        // `Vector3.Slerp`, `Color3.HSVtoRGB`); `instanceof` is unaffected.
        Vector2: withAllocatingStatics(Vector2),
        Vector3: withAllocatingStatics(Vector3),
        Vector4: withAllocatingStatics(Vector4),
        Color3: withAllocatingStatics(Color3),
        Color4: withAllocatingStatics(Color4),
        Matrix: withAllocatingStatics(Matrix),
        Quaternion: withAllocatingStatics(Quaternion),
        Path2, Path3D, Curve3, Axis, Space,
        Math,
        ...buildIdioms({ scratch, register, merge, material }),
        ...buildModifiers({ register, merge, retally, maxTriangles: MAX_TRIANGLES, warnings }),
    };

    return {
        scratch,
        api,
        live: () => created.filter(m => !m.isDisposed() && !booleanOperands.has(m)),
        anchors,
        triangles: () => triangles,
    };
}

export interface MeshBuildSession {
    /** Pass these bindings to the host's isolated executor. */
    api: Record<string, unknown>;
    warnings: string[];
    /** Bake the returned meshes (or live outputs when omitted), then dispose. */
    bake(returned?: unknown, assetName?: string): Promise<MeshBuildResult>;
    /** Release scratch geometry if the host's execution fails before baking. */
    dispose(): void;
}

/** Borrow only the host engine; all geometry is authored in glTF's right-handed space. */
export function createMeshBuild(hostScene: Scene): MeshBuildSession {
    const scratch = new Scene(hostScene.getEngine());
    scratch.useRightHandedSystem = true;
    const warnings: string[] = [];
    const { api, live, anchors, triangles } = buildApi(scratch, Date.now() + MAX_BUILD_MS, warnings);
    const bake = async (returned?: unknown, assetName = 'mesh'): Promise<MeshBuildResult> => {
        try {
            const chosen = new Set<Mesh>();
            collectReturned(returned, chosen);
            if (chosen.size === 0) {
                for (const m of live()) chosen.add(m);
            } else {
                for (const root of [...chosen]) {
                    for (const child of root.getChildMeshes()) if (isMesh(child)) chosen.add(child);
                }
            }
            const exportable = [...chosen].filter(m => !m.isDisposed() && m.getTotalVertices() > 0);
            if (exportable.length === 0) {
                return {
                    glb: null, stats: null, warnings,
                    errors: ['The build produced no geometry. Create meshes with MeshBuilder/fromPydata and return them (or just leave them undisposed).'],
                };
            }
            if (triangles() > MAX_TRIANGLES) {
                return {
                    glb: null, stats: null, warnings,
                    errors: [`Triangle budget exceeded (${Math.round(triangles()).toLocaleString()} > ${MAX_TRIANGLES.toLocaleString()}).`],
                };
            }
            if (exportable.length > MAX_EXPORT_MESHES) {
                return {
                    glb: null, stats: null, warnings,
                    errors: [`${exportable.length.toLocaleString()} separate meshes would be baked, over the ${MAX_EXPORT_MESHES.toLocaleString()} an asset may carry. `
                        + 'merge() the pieces that share a material — one merged mesh keeps a slot per material, so nothing is lost but the object count.'],
                };
            }

            // Same reason as the merge: an unmerged piece that reaches the
            // exporter without a material bakes as Babylon's grey default, whose
            // 0.5 glTF reads as linear (~sRGB 0.74), and `materialSlots` never
            // mentions it. One explicit slot, and the model is told.
            warnUnpainted(paintUnpainted(exportable, scratch), 'build', warnings);

            const stats = measure(exportable);
            if (anchors.length) stats.anchors = anchors.map(a => a.name.slice(ANCHOR_PREFIX.length));
            stats.diagnostics = diagnoseMeshes(exportable);
            warnings.push(...diagnosticWarnings(stats.diagnostics));
            const keep = new Set<unknown>([...exportable, ...anchors]);
            const result = await GLTF2Export.GLBAsync(scratch, assetName, {
                shouldExportNode: node => keep.has(node),
                exportWithoutWaitingForScene: true,
            });
            const file = result.glTFFiles[`${assetName}.glb`];
            const glb = file instanceof Blob ? file : new Blob([file], { type: 'model/gltf-binary' });
            return { glb, stats, errors: [], warnings };
        } catch (err) {
            return { glb: null, stats: null, errors: [err instanceof Error ? err.message : String(err)], warnings };
        } finally {
            scratch.dispose();
        }
    };
    return { api, warnings, bake, dispose: () => scratch.dispose() };
}

/** Triangle count, material slots and world bounds of what is about to bake. */
function measure(meshes: Mesh[]): MeshBuildStats {
    let triangles = 0;
    const slots: string[] = [];
    const min: [number, number, number] = [Infinity, Infinity, Infinity];
    const max: [number, number, number] = [-Infinity, -Infinity, -Infinity];

    // By identity, not by name: since material() takes the model's own
    // descriptive name, two distinct slots can legitimately share one (four
    // oaks in the chair, five limestones in the cottage wall), and counting
    // them as one would under-report the very variation we ask for.
    const seen = new Set<unknown>();
    const addSlot = (mat: { name: string }): void => {
        if (seen.has(mat)) return;
        seen.add(mat);
        slots.push(mat.name);
    };

    for (const mesh of meshes) {
        triangles += mesh.getTotalIndices() / 3;
        const mat = mesh.material;
        if (mat && 'subMaterials' in mat) {
            for (const sub of (mat as { subMaterials: ({ name: string } | null)[] }).subMaterials) {
                if (sub) addSlot(sub);
            }
        } else if (mat) addSlot(mat);

        mesh.computeWorldMatrix(true);
        mesh.refreshBoundingInfo();
        const box = mesh.getBoundingInfo().boundingBox;
        for (const axis of [0, 1, 2] as const) {
            min[axis] = Math.min(min[axis], box.minimumWorld.asArray()[axis]);
            max[axis] = Math.max(max[axis], box.maximumWorld.asArray()[axis]);
        }
    }
    if (!Number.isFinite(min[0])) return { ...EMPTY_STATS, triangles: Math.round(triangles), meshes: meshes.length };
    const round = (n: number): number => Math.round(n * 1000) / 1000;
    return {
        triangles: Math.round(triangles),
        meshes: meshes.length,
        materialSlots: slots,
        bounds: {
            min: [round(min[0]), round(min[1]), round(min[2])],
            max: [round(max[0]), round(max[1]), round(max[2])],
            size: [round(max[0] - min[0]), round(max[1] - min[1]), round(max[2] - min[2])],
        },
    };
}
